import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import messages from "../../../../../assets/locale/messages";
import { editEvent, getEventCrewSizePlans } from "../../../../../network/apis/events";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg, showSuccessMsg } from "../../../../../utils/Helpers";
import { UIActionModal, UICallout, UINumberField, UISelectCard } from "../../../../ui";
import {
  CUSTOM_CREW_SIZE_MIN,
  CUSTOM_PLAN_ERRORS,
  EMPTY_CUSTOM_CREW_PLAN,
  fillTemplate,
  formatAmount,
  getCustomCrewPlanState,
  toCrewSizeOption,
} from "../../../Form/Steps/ChooseCrewSize/crewSizeUtils";
import SummaryRow from "../../../Form/Steps/PublishAndPay/SummaryRow";
import {
  getCategoryUpgradeSummary,
  isExtendedEvent,
} from "../../CrewSize/customPlanUpgradeUtils";

import "../../../Form/Steps/PublishAndPay/PublishAndPay.scss";
import "../_CrewSizeUpgradeModals.scss";
import "../UpdateCustomPlanModal/UpdateCustomPlanModal.scss";

/**
 * Super Admin moves a published event on a standard plan to a higher tier, or (daily
 * events) to a custom size and rate. It applies at once through the event update, with no
 * approval (PROLINE-4107).
 */
const UpgradeCategoryModal = ({ open, event, currentPlan, onClose, onUpdated }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const tabT = events.viewEvents.crewSizeTab;
  const t = tabT.upgradeCategory;
  const summaryT = tabT.updateCustomPlan;
  const customT = events.creatEvent.crewSize.custom;
  const currency = events.creatEvent.crewSize.currency;

  /** Standard tiers above the current one, priced for this event. */
  const [options, setOptions] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [custom, setCustom] = useState(EMPTY_CUSTOM_CREW_PLAN);
  /** The custom plan's id, sent to move the event onto it. */
  const [customPlanId, setCustomPlanId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !event?.id) return undefined;
    let cancelled = false;
    getEventCrewSizePlans(event.id)
      .then((response) => {
        if (cancelled) return;
        const plans = response?.data?.data ?? [];
        setCustomPlanId(plans.find((plan) => plan.custom)?.id ?? null);
        const higher = plans
          .filter((plan) => !plan.custom)
          .map((plan) => toCrewSizeOption(plan))
          .filter((option) => option && option.max > currentPlan.max);
        setOptions(higher);
        setSelectedKey((key) => key ?? higher[0]?.key ?? null);
      })
      .catch((error) => {
        if (!cancelled) showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      });
    return () => {
      cancelled = true;
    };
  }, [open, event?.id, currentPlan.max, shared.generalErrMsg]);

  // Billed by `payment_plan`, frozen at publishing.
  const monthly = isExtendedEvent(event);
  const currentFee = monthly ? currentPlan.monthlyFee : currentPlan.dailyFee;
  const selected = options.find((option) => option.key === selectedKey);
  const customState = getCustomCrewPlanState(custom);
  /** Lowest custom size: above the standard tiers and above the current ceiling. */
  const minApplicants = Math.max(CUSTOM_CREW_SIZE_MIN, currentPlan.max + 1);
  const customAboveCurrent = Number(custom.applicants) > currentPlan.max;
  const customActive = !customState.isEmpty;
  const customValid = customState.isValid && customAboveCurrent;

  const newFee = customActive
    ? Number(custom.rate) || 0
    : (monthly ? selected?.monthlyFee : selected?.dailyFee) ?? 0;
  const newCeiling = customActive ? Number(custom.applicants) || 0 : selected?.max;
  // A custom rate is compared with the tier's daily fee, like a tier (PROLINE-4107 §3).
  const summary = getCategoryUpgradeSummary({ event, currentFee, newFee });
  const canConfirm = (customActive ? customValid : Boolean(selected)) && !saving;

  const confirm = async () => {
    setSaving(true);
    const data = customActive
      ? {
          crew_size_plan_id: customPlanId,
          custom_crew_size: Number(custom.applicants),
          custom_rate: Number(custom.rate),
        }
      : { crew_size_plan_id: selected.id };
    try {
      const response = await editEvent(
        { id: event.id, data },
        // The error is toasted once below, not also by the interceptor.
        { handlerEnabled: false },
      );
      // The backend's message, or ours while the update returns none.
      const message = response?.data?.message;
      showSuccessMsg(message || "crewSizeUpgradedSuccessfully", Boolean(message));
      onUpdated(response?.data?.data);
    } catch (error) {
      showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      setSaving(false);
    }
  };

  const applicantsErrorKey =
    customState.applicantsError ??
    (customActive && custom.applicants && !customAboveCurrent
      ? CUSTOM_PLAN_ERRORS.belowMinimum
      : null);
  const errorMessages = {
    [CUSTOM_PLAN_ERRORS.required]: customT.required,
    [CUSTOM_PLAN_ERRORS.belowMinimum]: customT.belowMinimum,
    [CUSTOM_PLAN_ERRORS.invalidRate]: customT.invalidRate,
  };

  // One plan at a time: a tier clears the Custom fields, typing in them clears the tier.
  const selectTier = (key) => {
    setSelectedKey(key);
    setCustom(EMPTY_CUSTOM_CREW_PLAN);
  };
  const changeCustom = (patch) => {
    setSelectedKey(null);
    setCustom((current) => ({ ...current, ...patch }));
  };

  const planName = customActive ? customT.title : selected?.name ?? "";
  const rateValue = fillTemplate(monthly ? t.monthlyRateValue : t.dailyRateValue, {
    rate: formatAmount(newFee),
    plan: planName,
  });

  return (
    <UIActionModal
      open={open}
      variant="info"
      className="crew-size-upgrade-modal"
      title={t.title}
      onClose={() => !saving && onClose()}
      actions={
        <UIActionModal.Actions
          cancelLabel={shared.cancel}
          confirmLabel={t.confirm}
          confirmDisabled={!canConfirm}
          onCancel={() => !saving && onClose()}
          onConfirm={confirm}
        />
      }
    >
      <UIActionModal.Divider />
      <div className="crew-size-upgrade__body">
        <Typography component="p" className="text-body-sm crew-size-upgrade__message">
          {t.description}
        </Typography>
        <Typography component="p" className="text-label-md crew-size-upgrade__current">
          {summaryT.current}{" "}
          <strong>
            {fillTemplate(t.currentValue, {
              size: currentPlan.name ?? "",
              max: formatAmount(currentPlan.max),
            })}
          </strong>
        </Typography>

        {options.length > 0 && (
          <div className="crew-size-upgrade__options" role="radiogroup" aria-label={t.title}>
            {options.map((option) => (
              <UISelectCard
                key={option.key}
                selectionRole="radio"
                size="md"
                title={option.name}
                meta={[
                  fillTemplate(tabT.range, {
                    min: formatAmount(option.min),
                    max: formatAmount(option.max),
                  }),
                ]}
                selected={option.key === selectedKey}
                onClick={() => selectTier(option.key)}
              />
            ))}
          </div>
        )}

        {/* Figma: the Customize section is offered on daily events only. */}
        {!monthly && (
          <section
            className="update-custom-plan__fields update-custom-plan__fields--card"
          >
            <Typography component="h3" className="text-heading-sm update-custom-plan__title">
              {t.customTitle}
            </Typography>
            <Typography component="p" className="text-body-sm update-custom-plan__subtitle">
              {t.customHint}
            </Typography>
            <div className="update-custom-plan__inputs">
              <UINumberField
                id="upgrade-category-applicants"
                name="upgrade_category_applicants"
                label={customT.applicants}
                required
                min={minApplicants}
                step={1}
                placeholder={formatAmount(minApplicants)}
                value={custom.applicants}
                onValueChange={(applicants) => changeCustom({ applicants })}
                error={Boolean(applicantsErrorKey)}
                helperText={errorMessages[applicantsErrorKey]}
              />
              <UINumberField
                id="upgrade-category-rate"
                name="upgrade_category_rate"
                label={customT.ratePerDay}
                required
                min={0}
                placeholder="0"
                value={custom.rate}
                onValueChange={(rate) => changeCustom({ rate })}
                error={Boolean(customState.rateError)}
                helperText={errorMessages[customState.rateError]}
              />
            </div>
          </section>
        )}

        {newCeiling ? (
          <div className="crew-size-upgrade__summary">
            <SummaryRow
              label={summaryT.newCeiling}
              value={fillTemplate(summaryT.newCeilingValue, {
                max: formatAmount(newCeiling),
                current: formatAmount(currentPlan.max),
              })}
            />
            {summary.remaining != null && (
              <SummaryRow
                label={monthly ? t.remainingMonths : summaryT.remainingDays}
                value={fillTemplate(
                  monthly ? t.remainingMonthsValue : summaryT.remainingDaysValue,
                  {
                    remaining: formatAmount(summary.remaining),
                    total: formatAmount(summary.total),
                  },
                )}
              />
            )}
            <SummaryRow label={summaryT.rate} value={rateValue} />
            <SummaryRow
              total
              label={summaryT.difference}
              value={
                summary.difference == null
                  ? null
                  : `${formatAmount(summary.difference)} ${currency}`
              }
            />
          </div>
        ) : null}

        <UICallout
          variant="primary"
          hasBorder={false}
          className="update-custom-plan__notice"
          icon={<InfoOutlinedIcon fontSize="small" />}
          title={summaryT.noticeTitle}
          description={t.noticeMessage}
        />
      </div>
    </UIActionModal>
  );
};

UpgradeCategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** The published event: its id, `payment_plan` and `remaining_operating_days`. */
  event: PropTypes.object.isRequired,
  /** The event's standard crew size: its name, ceiling and fees. */
  currentPlan: PropTypes.shape({
    name: PropTypes.string,
    max: PropTypes.number.isRequired,
    dailyFee: PropTypes.number,
    monthlyFee: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called with the event the backend returns once the upgrade is saved. */
  onUpdated: PropTypes.func.isRequired,
};

export default UpgradeCategoryModal;
